import React from "react";
import { Link } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { User, UserPlus, Shield, Users } from "lucide-react";

const LandingPage = () => {
  const entryOptions = [
    {
      id: "member",
      title: "Existing Member Login",
      description: "Access your member profile, directory, membership services, and account settings.",
      icon: User,
      href: "/login?role=member",
      color: "teal",
      testId: "member-login-btn"
    },
    {
      id: "register",
      title: "New Member Registration",
      description: "Start a new CPSI membership application and submit required details and documents.",
      icon: UserPlus,
      href: "/join",
      color: "emerald",
      testId: "new-member-btn"
    },
    {
      id: "admin",
      title: "Admin Login",
      description: "Secure access for authorised CPSI Secretariat and administrative users.",
      icon: Shield,
      href: "/login?role=admin",
      color: "slate",
      testId: "admin-login-btn"
    },
    {
      id: "committee",
      title: "Review Committee Login",
      description: "Secure access for authorised Review Committee members to review assigned applications.",
      icon: Users,
      href: "/login?role=committee",
      color: "violet",
      testId: "committee-login-btn"
    }
  ];

  const getColorClasses = (color) => {
    const colors = {
      teal: "hover:border-teal-300 hover:bg-teal-50/50",
      emerald: "hover:border-emerald-300 hover:bg-emerald-50/50",
      slate: "hover:border-slate-400 hover:bg-slate-50/50",
      violet: "hover:border-violet-300 hover:bg-violet-50/50"
    };
    return colors[color] || colors.slate;
  };

  const getIconColorClasses = (color) => {
    const colors = {
      teal: "text-teal-700 bg-teal-100",
      emerald: "text-emerald-700 bg-emerald-100",
      slate: "text-slate-700 bg-slate-200",
      violet: "text-violet-700 bg-violet-100"
    };
    return colors[color] || colors.slate;
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white" data-testid="landing-page">
      {/* Main Content */}
      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Welcome Section */}
        <div className="text-center mb-12">
          <h2 className="font-heading font-bold text-3xl sm:text-4xl text-slate-900 mb-4">
            CPSI Membership Portal
          </h2>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            Welcome to the CPSI Membership Portal. Apply for membership, manage your profile, 
            participate in reviews, and access authorised CPSI services.
          </p>
        </div>

        {/* Entry Options Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
          {entryOptions.map((option) => (
            <Link key={option.id} to={option.href} data-testid={option.testId}>
              <Card className={`h-full border-2 border-slate-200 transition-all duration-200 cursor-pointer ${getColorClasses(option.color)}`}>
                <CardHeader className="pb-2">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${getIconColorClasses(option.color)}`}>
                      <option.icon className="w-5 h-5" />
                    </div>
                    <CardTitle className="font-heading text-lg">{option.title}</CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  <CardDescription className="text-sm text-slate-600">
                    {option.description}
                  </CardDescription>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>

        {/* Footer Info */}
        <div className="mt-12 text-center">
          <p className="text-sm text-slate-500">
            Need help? Contact CPSI support at{" "}
            <a href="mailto:support@cpsindia.org" className="text-teal-700 hover:underline">
              support@cpsindia.org
            </a>
          </p>
        </div>
      </main>
    </div>
  );
};

export default LandingPage;
